/**
 * Learning Center API: the course builder (docs/plans/lms-course-builder-plan.md
 * Appendix A and D, 4.7, 5.1 to 5.4, 6.4, 6.12, 8, 9.4; WP-B21's routes,
 * WP-B05's client). Typed calls over services/api (the shared axios instance
 * with the JWT and X-Site-Id); nothing under src/lms imports axios (LMS plan
 * 2.3a). Every route is an lms admin route behind the guard, requireLmsAdmin
 * and the builder switch: while LMS_COURSE_BUILDER_ENABLED is unset every one
 * answers 503 LMS_BUILDER_DISABLED, and outside the network allow-list 403
 * LMS_BUILDER_NETWORK_NOT_ALLOWED (the screens read GET /health's builder
 * block before they render an entry point, plan G20).
 *
 *   GET  /admin/builder/config                           -> BuilderConfig
 *   POST /admin/builder/runs (multipart)                 -> 201 { run }
 *   GET  /admin/builder/runs?course_id=&status=&limit=&offset= -> { items: run summaries, page }
 *   GET  /admin/builder/runs/:id                         -> BuilderRunDetail
 *   POST /admin/builder/runs/:id/start                   -> { run }
 *   POST /admin/builder/runs/:id/cancel                  -> { run }
 *   POST /admin/builder/versions/:versionId/regenerate   -> 201 { run } (JSON, or multipart with one file)
 *   GET  /admin/builder/versions/:versionId/provenance   -> { items }
 *   GET  /admin/builder/versions/:versionId/run          -> { run } | null
 *   GET  /admin/builder/sources/:id/url                  -> { url, filename } (presigned, 10 minutes)
 *   POST /admin/builder/sources/:id/withdraw {reason}    -> { source }
 *   GET  /admin/builder/versions/:versionId/clinical-signoff -> { signoff, required, current } (18.2 Q16)
 *   POST /admin/builder/versions/:versionId/clinical-signoff -> 201 { signoff } (password re-check)
 *
 * Query strings travel as axios `params`, so the URL stays the path (the test
 * harness routes GETs by exact URL). A multipart body is a FormData posted with
 * no explicit Content-Type: the browser sets the boundary.
 */
import api from '../../services/api';
import type { CourseCategory, CourseKind } from '../content/types';
import { apiErrorCode, apiErrorMessage } from './client';
import type { LmsList } from './client';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Bounds (6.2, 8.2, 8.4, Appendix B defaults; the server re-checks all of them)
// ---------------------------------------------------------------------------

export const BUILDER_PDF_MIME = 'application/pdf';
export const BUILDER_DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const BUILDER_TEXT_MIME = 'text/plain';
export const BUILDER_MARKDOWN_MIME = 'text/markdown';
/** 5.2 lms_builder_sources.mime; the config route's accepted_types is the live list. */
export const BUILDER_FILE_MIMES: readonly string[] = [
  BUILDER_PDF_MIME,
  BUILDER_DOCX_MIME,
  BUILDER_TEXT_MIME,
  BUILDER_MARKDOWN_MIME,
];
/** The file input's accept attribute (a .md file often reaches the browser with no type). */
export const BUILDER_FILE_ACCEPT = '.pdf,.docx,.txt,.md,' + BUILDER_FILE_MIMES.join(',');
/**
 * 18.2 Q3: LMS_BUILDER_MAX_FILES defaults to 12 and counts every source of a
 * run (files plus web links); the config route's limits.max_files is the live value.
 */
export const BUILDER_DEFAULT_MAX_FILES = 12;
/** 18.2 Q3: 50 MB per file (storage kind 'builder'); the config route's limits.max_bytes_per_file is the live value. */
export const BUILDER_FILE_MAX_BYTES = 50 * 1024 * 1024;
/** 18.2 Q7: a web link is https only and at most 2048 characters (the server judges the host). */
export const BUILDER_LINK_MAX_CHARS = 2048;
/** 8.2, 8.4: an instruction is at most 1000 characters. */
export const BUILDER_INSTRUCTION_MAX = 1000;
/** 6.2: the audience line is at most 300 characters. */
export const BUILDER_AUDIENCE_MAX = 300;
/** 6.2, 8.2: the course lengths the admin may choose. */
export const BUILDER_TARGET_MINUTES = [30, 45, 60, 90] as const;
/** 6.2, 8.2: the pool sizes the admin may choose (the draw is half). */
export const BUILDER_POOL_SIZES = [20, 30, 45] as const;
/** 18.2 Q12: a best-practice scenario runs 3 to 6 minutes. */
export const BUILDER_SCENARIO_TARGET_MINUTES = [3, 4, 5, 6] as const;
/** 18.2 Q12: a scenario's pool is 3 to 6 questions, all drawn. */
export const BUILDER_SCENARIO_POOL_SIZES = [3, 4, 5, 6] as const;
/** Appendix A: a list page holds at most 200 rows. */
export const BUILDER_RUNS_MAX_LIMIT = 200;

// ---------------------------------------------------------------------------
// Shapes (5.1 to 5.4, Appendix A, Appendix D)
// ---------------------------------------------------------------------------

/** 5.1 lms_builder_runs.mode. */
export type BuilderRunMode = 'new' | 'extend' | 'section';

/** 5.1 lms_builder_runs.status. */
export type BuilderRunStatus =
  | 'uploaded'
  | 'extracting'
  | 'estimated'
  | 'queued'
  | 'structuring'
  | 'authoring'
  | 'assembling'
  | 'repairing'
  | 'writing'
  | 'done'
  | 'failed'
  | 'cancelled';

export const BUILDER_TERMINAL_STATUSES: readonly BuilderRunStatus[] = ['done', 'failed', 'cancelled'];

/** 6.1: the stages in order. */
export type BuilderStage = 'upload' | 'extract' | 'estimate' | 'structure' | 'author' | 'assemble' | 'repair' | 'write';

/** True for done, failed and cancelled (the polls stop there, 4.10). */
export function isTerminalRunStatus(status: string | null | undefined): boolean {
  return (BUILDER_TERMINAL_STATUSES as readonly string[]).includes(String(status));
}

/** Appendix D sources_meta entry: the admin's optional facts about the file at an ordinal (1-based, upload order). */
export interface BuilderSourceMeta {
  ordinal: number;
  title?: string | null;
  publisher?: string | null;
  public_url?: string | null;
}

/** Appendix D extra_sources entry: a citation without a file (7.3). */
export interface BuilderExtraSource {
  title: string;
  publisher: string;
  url: string;
}

/** 18.2 Q7: a link source (an HTTPS address the worker fetches once, instead of a file). */
export interface BuilderSourceLink {
  url: string;
  title?: string | null;
}

/** Appendix D targets as the run summary carries them (the attestation text is left out). */
export interface BuilderTargets {
  title?: string | null;
  category?: CourseCategory;
  kind?: CourseKind;
  validity_months?: number | null;
  renewal_lead_days?: number | null;
  audience?: string | null;
  /** 18.5: may be null in extend mode (the course fixes it). */
  target_minutes?: number | null;
  pool_size?: number | null;
  instruction?: string | null;
  course_id?: string | null;
  replace_section_ids?: string[];
  keep_closing?: boolean;
  section_id?: string | null;
  sources_meta?: BuilderSourceMeta[];
  extra_sources?: BuilderExtraSource[];
  /** 18.2 Q7. */
  source_links?: BuilderSourceLink[];
  /** A section run's parent run (WP-B21). */
  parent_run_id?: string | null;
  attestation?: { actor_id?: string | null; at?: string | null } | null;
}

/** 6.4 lms_builder_runs.estimate. */
export interface BuilderEstimate {
  source_tokens: number;
  pages?: number | null;
  sections_planned: number;
  cents: number;
  model_author: string;
  model_extract: string;
  caps?: { run: number; daily_remaining: number; monthly_remaining: number };
  computed_at?: string;
}

/** 5.1, 6.12 lms_builder_runs.progress. */
export interface BuilderProgress {
  stage?: string | null;
  sections_total?: number | null;
  sections_done?: number | null;
  repairs_done?: number | null;
  spent_cents?: number | null;
  message?: string | null;
  /** 18.5: the part being written or repaired, in words ("section 3", "the closing section"), or null. */
  section?: string | null;
  /** 18.5: the assembler's warnings for the admin (plain sentences). */
  warnings?: string[] | null;
  updated_at?: string | null;
}

/** Appendix D run summary (the API's run object). */
export interface BuilderRun {
  id: string;
  network_id: string;
  course_id: string | null;
  version_id: string | null;
  mode: BuilderRunMode;
  status: BuilderRunStatus;
  stage: string | null;
  targets: BuilderTargets;
  estimate: BuilderEstimate | null;
  estimate_cents: number | null;
  /** numeric(10,4) on the row: may arrive as a string through PostgREST; read it with centsOf. */
  spent_cents: number | string;
  progress: BuilderProgress;
  lint: { ok: boolean; count: number } | null;
  error: string | null;
  error_code: string | null;
  models: { author: string | null; extract: string | null };
  attempts: number;
  requested_by: { id: string; full_name: string | null };
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  cancel_requested_at: string | null;
}

/** 5.2 lms_builder_sources.kind. */
export type BuilderSourceKind = 'pdf' | 'docx' | 'text' | 'markdown';

/** 5.2 lms_builder_sources.extraction_status. */
export type BuilderExtractionStatus = 'pending' | 'done' | 'refused' | 'failed';

/** A source summary (5.2 without the storage key). */
export interface BuilderSource {
  id: string;
  run_id: string;
  ordinal: number;
  filename: string;
  mime: string;
  size_bytes: number;
  sha256?: string;
  kind: BuilderSourceKind | null;
  page_count: number | null;
  text_chars: number | null;
  token_estimate: number | null;
  extraction_status: BuilderExtractionStatus;
  extraction_note: string | null;
  public_url: string | null;
  title: string | null;
  withdrawn_at: string | null;
  uploaded_by?: string | null;
  created_at?: string | null;
}

/** 5.4 lms_builder_usage_events.kind. */
export type BuilderUsageKind =
  | 'structure'
  | 'author'
  | 'closing'
  | 'course_level'
  | 'repair'
  | 'instruct'
  | 'merge_plan'
  | 'probe';

/** GET /admin/builder/runs/:id. */
export interface BuilderRunDetail {
  run: BuilderRun;
  sources: BuilderSource[];
  estimate: BuilderEstimate | null;
  progress: BuilderProgress;
  lint: { ok: boolean; count: number } | null;
  usage: { calls: number; cents: number | string; by_kind: Partial<Record<BuilderUsageKind, number | string>> };
}

/** GET /admin/builder/config. */
export interface BuilderConfig {
  enabled: boolean;
  network_allowed: boolean;
  models: { author: string | null; extract: string | null };
  limits: {
    max_files: number;
    max_bytes_per_file: number;
    max_source_tokens: number;
    max_section_runs_per_version: number;
    repair_rounds: number;
  };
  accepted_types: string[];
  caps: {
    run_cents: number;
    daily_cents: number;
    monthly_cents: number;
    daily_remaining_cents: number;
    monthly_remaining_cents: number;
  };
  source_minimum: { compliance: number; sop: number; other: number };
}

/**
 * GET /api/lms/health's builder block (plan 4.7). The LmsHealth type in
 * api/client.ts gains it (WP-B04); the shape is repeated here so the builder
 * screens can name it from one module.
 */
export interface LmsBuilderHealth {
  enabled: boolean;
  network_allowed: boolean;
  models: { author: string | null; extract: string | null };
  queue: 'ready' | 'unavailable';
  active_runs: number;
  last_run: { id: string; status: BuilderRunStatus; finished_at: string | null } | null;
  /**
   * The course editor, "Edit with AI" (editor plan 4.7, 18.2 Q15; WP-E03):
   * enabled when LMS_COURSE_BUILDER_ENABLED is the literal 'true' and the
   * off-lever LMS_COURSE_EDITOR_ENABLED does not read 'false' (unset reads
   * on). Optional so a response from an older api reads as "no editor"
   * (callers check editor?.enabled === true).
   */
  editor?: { enabled: boolean };
}

/** 5.3 lms_builder_provenance.element_kind. */
export type ProvenanceElementKind =
  | 'objective'
  | 'section'
  | 'slide'
  | 'reading_paragraph'
  | 'question'
  | 'takeaway'
  | 'glossary'
  | 'not_covered';

/** Appendix D locator. */
export interface ProvenanceLocator {
  unit?: number | null;
  page?: number | null;
  heading_path?: string[] | null;
  char_start?: number | null;
  char_end?: number | null;
  /** At most 300 characters, a verbatim substring of the extracted text when verified. */
  quote?: string | null;
}

/** One GET /admin/builder/versions/:versionId/provenance item (the newest row per element_ref). */
export interface ProvenanceItem {
  element_kind: ProvenanceElementKind;
  /** 'o0', 's3', 's3-2', 's3:p4', 'q12', 't2', 'g:PHI' (5.3). */
  element_ref: string;
  source: { id: string; filename: string; title: string | null; withdrawn: boolean } | null;
  locator: ProvenanceLocator;
  verified: boolean;
}

/** A presigned URL answer (LMS plan 2.3a "Downloads"). */
export interface BuilderSourceUrl {
  url: string;
  filename: string;
}

/** The fields shared by both create modes (Appendix A POST /admin/builder/runs). */
interface CreateRunCommon {
  files: File[];
  instruction?: string | null;
  sourcesMeta?: BuilderSourceMeta[];
  extraSources?: BuilderExtraSource[];
  /** 18.2 Q7: web links, sent as the multipart field source_links (files plus links at most max_files). */
  sourceLinks?: BuilderSourceLink[];
  /** 9.2: the policy checkbox; the server refuses anything but 'true' (LMS_BUILDER_ATTESTATION_REQUIRED). */
  attestation: boolean;
}

/** Mode new: a new course from the files. */
export interface CreateNewRunInput extends CreateRunCommon {
  mode: 'new';
  title?: string | null;
  category: CourseCategory;
  kind: CourseKind;
  validity_months?: number | null;
  renewal_lead_days?: number | null;
  audience?: string | null;
  target_minutes: number;
  pool_size: number;
}

/** Mode extend: the files built into a new draft of an existing course (6.10). */
export interface CreateExtendRunInput extends CreateRunCommon {
  mode: 'extend';
  course_id: string;
  /** Appendix A lists category, kind, target_minutes and pool_size for both modes; sent when given. */
  category?: CourseCategory;
  kind?: CourseKind;
  validity_months?: number | null;
  renewal_lead_days?: number | null;
  audience?: string | null;
  target_minutes?: number;
  pool_size?: number;
  replace_section_ids: string[];
  keep_closing: boolean;
}

export type CreateRunInput = CreateNewRunInput | CreateExtendRunInput;

export interface ListRunsQuery {
  courseId?: string | null;
  status?: BuilderRunStatus | null;
  limit?: number;
  offset?: number;
}

export interface RegenerateSectionInput {
  section_id: string;
  instruction?: string | null;
  /** Only for a draft with no parent run (Appendix A): the one source file; the body is multipart then. */
  file?: File | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A cents value that may arrive as a numeric string (numeric(10,4)); 0 when unreadable. */
export function centsOf(value: unknown): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Cents as dollars for the screens ("$4.80"); sub-cent spend rounds to the cent. */
export function formatCents(value: unknown): string {
  const dollars = Math.round(centsOf(value)) / 100;
  return `$${dollars.toFixed(2)}`;
}

function trimmedOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function appendNumber(form: FormData, name: string, value: number | null | undefined): void {
  if (typeof value === 'number' && Number.isFinite(value)) form.append(name, String(value));
}

/**
 * The multipart body of POST /admin/builder/runs (Appendix A): files[] in
 * upload order (the ordinal is the 1-based position), the steering fields as
 * strings, the arrays as JSON, attestation 'true' only when ticked. Empty
 * optionals are left out so the server applies its own default.
 */
export function buildCreateRunForm(input: CreateRunInput): FormData {
  const form = new FormData();
  for (const file of input.files) form.append('files[]', file);
  form.append('mode', input.mode);

  if (input.mode === 'new') {
    const title = trimmedOrEmpty(input.title);
    if (title) form.append('title', title);
  } else {
    form.append('course_id', input.course_id);
    form.append('replace_section_ids', JSON.stringify(input.replace_section_ids ?? []));
    form.append('keep_closing', input.keep_closing ? 'true' : 'false');
  }
  if (input.category) form.append('category', input.category);
  if (input.kind) form.append('kind', input.kind);
  appendNumber(form, 'validity_months', input.validity_months);
  appendNumber(form, 'renewal_lead_days', input.renewal_lead_days);
  const audience = trimmedOrEmpty(input.audience);
  if (audience) form.append('audience', audience);
  appendNumber(form, 'target_minutes', input.target_minutes);
  appendNumber(form, 'pool_size', input.pool_size);
  const instruction = trimmedOrEmpty(input.instruction);
  if (instruction) form.append('instruction', instruction);
  if (input.sourcesMeta && input.sourcesMeta.length > 0) {
    form.append('sources_meta', JSON.stringify(input.sourcesMeta));
  }
  if (input.extraSources && input.extraSources.length > 0) {
    form.append('extra_sources', JSON.stringify(input.extraSources));
  }
  if (input.sourceLinks && input.sourceLinks.length > 0) {
    const links = input.sourceLinks.map((link) => {
      const title = trimmedOrEmpty(link.title);
      return title ? { url: link.url.trim(), title } : { url: link.url.trim() };
    });
    form.append('source_links', JSON.stringify(links));
  }
  form.append('attestation', input.attestation ? 'true' : 'false');
  return form;
}

/** The multipart body of a regenerate with a file (Appendix A: one optional file plus the JSON fields). */
export function buildRegenerateForm(input: RegenerateSectionInput): FormData {
  const form = new FormData();
  if (input.file) form.append('file', input.file);
  form.append('section_id', input.section_id);
  const instruction = trimmedOrEmpty(input.instruction);
  if (instruction) form.append('instruction', instruction);
  return form;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET /api/lms/admin/builder/config (the wizard's limits, caps and source minimum). */
export async function getBuilderConfig(): Promise<BuilderConfig> {
  const response = await api.get<BuilderConfig>('/api/lms/admin/builder/config');
  return response.data;
}

/** POST /api/lms/admin/builder/runs (multipart; 201 { run }, the run at status uploaded). */
export async function createBuilderRun(input: CreateRunInput): Promise<BuilderRun> {
  const response = await api.post<{ run: BuilderRun }>('/api/lms/admin/builder/runs', buildCreateRunForm(input));
  return response.data.run;
}

/** GET /api/lms/admin/builder/runs (newest first; limit capped at 200). */
export async function listBuilderRuns(query: ListRunsQuery = {}): Promise<LmsList<BuilderRun>> {
  const params: Record<string, string | number> = {};
  if (query.courseId) params.course_id = query.courseId;
  if (query.status) params.status = query.status;
  params.limit = Math.min(Math.max(1, query.limit ?? BUILDER_RUNS_MAX_LIMIT), BUILDER_RUNS_MAX_LIMIT);
  if (query.offset) params.offset = query.offset;
  const response = await api.get<LmsList<BuilderRun>>('/api/lms/admin/builder/runs', { params });
  return response.data;
}

/** GET /api/lms/admin/builder/runs/:id (the run, its sources, estimate, progress, lint and spend). */
export async function getBuilderRun(runId: string): Promise<BuilderRunDetail> {
  const response = await api.get<BuilderRunDetail>(`/api/lms/admin/builder/runs/${enc(runId)}`);
  return response.data;
}

/** POST /api/lms/admin/builder/runs/:id/start (from estimated, or a retry of a failed run; 6.4, 6.12). */
export async function startBuilderRun(runId: string): Promise<BuilderRun> {
  const response = await api.post<{ run: BuilderRun }>(`/api/lms/admin/builder/runs/${enc(runId)}/start`);
  return response.data.run;
}

/** POST /api/lms/admin/builder/runs/:id/cancel (queued: cancelled at once; active: cancel requested). */
export async function cancelBuilderRun(runId: string): Promise<BuilderRun> {
  const response = await api.post<{ run: BuilderRun }>(`/api/lms/admin/builder/runs/${enc(runId)}/cancel`);
  return response.data.run;
}

/**
 * POST /api/lms/admin/builder/versions/:versionId/regenerate (8.4, 6.11):
 * JSON { section_id, instruction? }, or multipart when a file is given (a
 * draft with no parent run). 201 { run } in mode section.
 */
export async function regenerateSection(versionId: string, input: RegenerateSectionInput): Promise<BuilderRun> {
  const url = `/api/lms/admin/builder/versions/${enc(versionId)}/regenerate`;
  let body: FormData | { section_id: string; instruction?: string };
  if (input.file) {
    body = buildRegenerateForm(input);
  } else {
    const json: { section_id: string; instruction?: string } = { section_id: input.section_id };
    const instruction = trimmedOrEmpty(input.instruction);
    if (instruction) json.instruction = instruction;
    body = json;
  }
  const response = await api.post<{ run: BuilderRun }>(url, body);
  return response.data.run;
}

/** GET /api/lms/admin/builder/versions/:versionId/provenance (the Sources panel, 8.3). */
export async function getVersionProvenance(versionId: string): Promise<ProvenanceItem[]> {
  const response = await api.get<{ items: ProvenanceItem[] }>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/provenance`
  );
  return Array.isArray(response.data?.items) ? response.data.items : [];
}

/**
 * GET /api/lms/admin/builder/versions/:versionId/run: the active or last run
 * for the version (the editor's notice and read-only banner), or null. The
 * route answers { run } or a bare null; both read as the run or null.
 */
export async function getVersionRun(versionId: string): Promise<BuilderRun | null> {
  const response = await api.get<{ run: BuilderRun | null } | null>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/run`
  );
  const data = response.data;
  return data && typeof data === 'object' && data.run ? data.run : null;
}

/** GET /api/lms/admin/builder/sources/:id/url (presigned, inline, 10 minutes; 404 when withdrawn). */
export async function getBuilderSourceUrl(sourceId: string): Promise<BuilderSourceUrl> {
  const response = await api.get<BuilderSourceUrl>(`/api/lms/admin/builder/sources/${enc(sourceId)}/url`);
  return response.data;
}

/** POST /api/lms/admin/builder/sources/:id/withdraw {reason} (9.4). */
export async function withdrawBuilderSource(sourceId: string, reason: string): Promise<BuilderSource> {
  const response = await api.post<{ source: BuilderSource }>(
    `/api/lms/admin/builder/sources/${enc(sourceId)}/withdraw`,
    { reason: reason.trim() }
  );
  return response.data.source;
}

/** 18.2 Q16: a recorded clinician sign-off. */
export interface ClinicalSignoff {
  clinician_name: string | null;
  statement: string | null;
  signed_by: { id: string | null; full_name: string | null };
  signed_at: string | null;
}

/** GET /admin/builder/versions/:versionId/clinical-signoff (18.2 Q16, 18.5). */
export interface ClinicalSignoffState {
  signoff: ClinicalSignoff | null;
  /** True for a builder-written clinical_skills draft. */
  required: boolean;
  /** True only when the sign-off is later than the newest builder write of the version. */
  current: boolean;
}

export interface RecordClinicalSignoffInput {
  clinician_name: string;
  statement: string;
  password: string;
}

/** 18.2 Q16: the lengths the server checks. */
export const CLINICIAN_NAME_RANGE = [2, 120] as const;
export const SIGNOFF_STATEMENT_RANGE = [10, 500] as const;

/**
 * GET /api/lms/admin/builder/versions/:versionId/clinical-signoff. A version
 * no builder run wrote answers { signoff: null, required: false, current: false } (18.5).
 */
export async function getClinicalSignoff(versionId: string): Promise<ClinicalSignoffState> {
  const response = await api.get<ClinicalSignoffState | null>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/clinical-signoff`
  );
  const data = response.data;
  return {
    signoff: data && data.signoff ? data.signoff : null,
    required: Boolean(data && data.required),
    current: Boolean(data && data.current),
  };
}

/**
 * POST /api/lms/admin/builder/versions/:versionId/clinical-signoff with the
 * caller's password (the credential re-check, 401 LMS_REAUTH_FAILED when it
 * is wrong). The password goes in the body once: `_retry: true` stops the
 * shared client from replaying the request after a session refresh (LMS plan 18.22).
 */
export async function recordClinicalSignoff(
  versionId: string,
  input: RecordClinicalSignoffInput
): Promise<ClinicalSignoff> {
  const config: Record<string, unknown> = { _retry: true };
  const response = await api.post<{ signoff: ClinicalSignoff }>(
    `/api/lms/admin/builder/versions/${enc(versionId)}/clinical-signoff`,
    {
      clinician_name: input.clinician_name.trim(),
      statement: input.statement.trim(),
      password: input.password,
    },
    config
  );
  return response.data.signoff;
}

// ---------------------------------------------------------------------------
// Error readers (Appendix A codes; runModel.ts maps a code to plain words)
// ---------------------------------------------------------------------------

/** Every builder-specific code a route or a run's error_code can carry (Appendix A, 6.12). */
export const BUILDER_ERROR_CODES = [
  'LMS_BUILDER_DISABLED',
  'LMS_BUILDER_NETWORK_NOT_ALLOWED',
  'LMS_BUILDER_UNSUPPORTED_FILE',
  'LMS_BUILDER_SOURCES_INSUFFICIENT',
  'LMS_BUILDER_ATTESTATION_REQUIRED',
  'LMS_BUILDER_SOURCE_REFUSED',
  'LMS_BUILDER_NO_SOURCES',
  'LMS_BUILDER_SECTION_LIMIT',
  'LMS_BUILDER_QUEUE_UNAVAILABLE',
  'LMS_BUILDER_RATE_LIMITED',
  'LMS_BUILDER_RATE_LIMIT_UNAVAILABLE',
  'LMS_BUILDER_BUDGET_CHECK_UNAVAILABLE',
  'LMS_BUILDER_RUN_CAP_EXCEEDED',
  'LMS_BUILDER_DAILY_BUDGET_EXCEEDED',
  'LMS_BUILDER_MONTHLY_BUDGET_EXCEEDED',
  'LMS_BUILDER_MODEL_OUTPUT_INVALID',
  'LMS_BUILDER_MODEL_REFUSED',
  // 18.7: an answer cut off at the model's output limit (never re-asked).
  'LMS_BUILDER_MODEL_OUTPUT_TRUNCATED',
  // 18.3, 18.4, 18.5: the codes the landed pipeline, routes and gateway also carry.
  'LMS_BUILDER_LINK_FETCH_FAILED',
  'LMS_BUILDER_UPLOAD_FAILED',
  'LMS_BUILDER_BAD_REQUEST',
  'LMS_BUILDER_MODEL_TIMEOUT',
  'LMS_BUILDER_MODEL_ERROR',
  'LMS_BUILDER_MODEL_ACCESS_DENIED',
  'LMS_BUILDER_MODEL_UNSUPPORTED',
  'LMS_BUILDER_ASSEMBLY_INPUT_INVALID',
  'LMS_BUILDER_MERGE_PLAN_INVALID',
  'LMS_BUILDER_ARTIFACT_MISSING',
  'LMS_BUILDER_JOB_FAILED',
  'LMS_BUILDER_JOB_INVALID',
  'LMS_BUILDER_PIPELINE_UNAVAILABLE',
  // records/reauth.js: the clinician sign-off's password re-check failed (401; 18.3).
  'LMS_REAUTH_FAILED',
] as const;

export type BuilderErrorCode = (typeof BUILDER_ERROR_CODES)[number];

/** The spend guards' codes (G16): a cap or a rate limit reached, or a guard that could not be checked. */
export const BUILDER_GUARD_CODES: readonly BuilderErrorCode[] = [
  'LMS_BUILDER_RATE_LIMITED',
  'LMS_BUILDER_RATE_LIMIT_UNAVAILABLE',
  'LMS_BUILDER_BUDGET_CHECK_UNAVAILABLE',
  'LMS_BUILDER_RUN_CAP_EXCEEDED',
  'LMS_BUILDER_DAILY_BUDGET_EXCEEDED',
  'LMS_BUILDER_MONTHLY_BUDGET_EXCEEDED',
];

/** True when `code` is one of the builder's own codes. */
export function isBuilderErrorCode(code: unknown): code is BuilderErrorCode {
  return typeof code === 'string' && (BUILDER_ERROR_CODES as readonly string[]).includes(code);
}

/** A failed builder call, read once: HTTP status, code (builder or generic LMS code) and the server's message. */
export interface BuilderApiError {
  status: number | null;
  code: string | null;
  message: string;
}

/** Read an axios-shaped rejection into { status, code, message }; `fallback` when the server sent no message. */
export function readBuilderError(err: unknown, fallback = 'The course builder could not complete the request.'): BuilderApiError {
  const e = err as { response?: { status?: unknown } } | null | undefined;
  const raw = e?.response?.status;
  const status = typeof raw === 'number' ? raw : null;
  return { status, code: apiErrorCode(err), message: apiErrorMessage(err, fallback) };
}

/** The switch is off (503 LMS_BUILDER_DISABLED): the screen should hide the builder, not show a failure. */
export function isBuilderDisabledError(err: unknown): boolean {
  return apiErrorCode(err) === 'LMS_BUILDER_DISABLED';
}

/** A spend guard refused the call (429 or 503 with a G16 code). */
export function isBuilderGuardError(err: unknown): boolean {
  const code = apiErrorCode(err);
  return code !== null && (BUILDER_GUARD_CODES as readonly string[]).includes(code);
}
